import React, { useState } from 'react';
import { X, Plus, Minus, Star, Clock, ChefHat } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const QuantityModal = ({ isOpen, onClose, item, onAddToCart }) => {
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('half');

  if (!isOpen || !item) return null;

  const handleAddToCart = () => {
    console.log('🛒 QuantityModal: Adding to cart with:', { item, selectedSize, selectedQuantity });
    onAddToCart(item, selectedQuantity, selectedSize);
    onClose();
  };

  const quantityOptions = [
    { 
      id: 'half', 
      label: 'Half', 
      price: typeof item.price === 'object' && item.price !== null ? 
        (Number(item.price.half) || 0) : 
        (Number(item.price) || 0) 
    },
    { 
      id: 'full', 
      label: 'Full', 
      price: typeof item.price === 'object' && item.price !== null ? 
        (Number(item.price.full) || 0) : 
        (Number(item.price) * 1.5 || 0) 
    }
  ];

  // Calculate total price
  const selectedOption = quantityOptions.find(option => option.id === selectedSize);
  const totalPrice = selectedOption ? 
    (Number(selectedOption.price) || 0) * (Number(selectedQuantity) || 1) : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '20px 20px 0 0',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              backgroundColor: '#f8fafc'
            }}>
              <div style={{ position: 'relative' }}>
                <img
                  src={item.image}
                  alt={item.name}
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '16px',
                    objectFit: 'cover',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/90x90/ff6b35/ffffff?text=${encodeURIComponent(item.name)}`;
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  <ChefHat size={12} color="#6b7280" />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {item.name}
                  {item.isPopular && (
                    <Star size={16} color="#fbbf24" fill="#fbbf24" />
                  )}
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '4px'
                }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      backgroundColor: 'rgba(251, 191, 36, 0.1)',
                      borderRadius: '12px',
                      border: '1px solid rgba(251, 191, 36, 0.2)'
                    }}>
                      <div style={{
                        backgroundColor: '#fbbf24',
                        borderRadius: '50%',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Star size={12} color="white" fill="white" />
                      </div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#92400e'
                      }}>
                        {item.rating || '4.5'}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '12px',
                      border: '1px solid rgba(99, 102, 241, 0.2)'
                    }}>
                      <div style={{
                        backgroundColor: '#6366f1',
                        borderRadius: '50%',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Clock size={12} color="white" />
                      </div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#4338ca'
                      }}>
                        {item.prepTime || '25-30 mins'}
                      </span>
                    </div>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  Customize your order
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  padding: '10px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '20px', maxHeight: '50vh', overflowY: 'auto' }}>
              {/* Quantity Section */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 8px 0'
                }}>
                  Quantity
                </h4>
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: '0 0 16px 0'
                }}>
                  Required • Select any 1 option
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {quantityOptions.map((option) => (
                    <div
                      key={option.id}
                      onClick={() => setSelectedSize(option.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        border: selectedSize === option.id ? '2px solid #ff6b35' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        backgroundColor: selectedSize === option.id ? '#fff7ed' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 2px 0'
                        }}>
                          {option.label}
                        </p>
                        <p style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          margin: 0
                        }}>
                          {option.id === 'half' ? 'Serves 1 (300 Ml)' : 'Serves 2 (500 Ml)'}
                        </p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: '#1f2937'
                        }}>
                          ₹{option.price}
                        </span>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          border: selectedSize === option.id ? '2px solid #ff6b35' : '2px solid #d1d5db',
                          backgroundColor: selectedSize === option.id ? '#ff6b35' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {selectedSize === option.id && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: 'white'
                            }} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Action Bar */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              {/* Quantity Selector */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white'
              }}>
                <button
                  onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Minus size={16} />
                </button>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {selectedQuantity}
                </span>
                <button
                  onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Plus size={16} />
                </button>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
              >
                Add item ₹{totalPrice}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuantityModal;
